import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';
import { searchResultsInfo } from '../../../../shared/core/shared.selectors';
import { SearchService } from '../../core/search.service';
import * as moment from 'moment/moment';
import {
  ALL_RESULTS_SEARCH_TYPE,
  SEARCH_ENTITIES,
  SearchEntityConfig,
  searchEntityBySearchType,
} from '../../../../shared/constants/search-entities';

/**
 * Generic, registry-driven results list for one search entity type.
 * Replaces the (almost identical) daily-reports-list and issues-list
 * components: the bespoke daily-report/issue cards are plugged in via the
 * registry `card` key, every other entity type uses the generic card.
 */
@Component({
    selector: 'app-search-results-list',
    templateUrl: './search-results-list.component.html',
    styleUrls: ['./search-results-list.component.scss'],
    standalone: false
})
export class SearchResultsListComponent implements OnInit, OnDestroy {
  @Input() entityType: string;

  config: SearchEntityConfig;
  /** The cross-category tab: rows come from every type, ranked by relevance. */
  isAllTab = false;
  // How many rows the All tab merges in. It is a "best matches" view, not a
  // full listing — the category tabs are where you page through a type.
  readonly allTabLimit = 20;
  resultsList: any[] = [];
  // Mirrors the api's PER_INDEX_RESULT_WINDOW (search-service.js): each
  // entity bucket carries at most this many hits per response, and the
  // paginator below only pages the already-fetched array — it can never
  // reach deeper matches. When the bucket arrives full, say so instead of
  // presenting a complete-looking paginator over a truncated set.
  readonly apiResultWindow = 25;
  itemsPerPage = 10;
  currentPage = 1;
  paginatedResultsList: any[];
  filterResultsList: any[] = [];
  startDate: any;
  endDate: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private searchService: SearchService,
    private commonService: CommonService,
  ) {
  }

  ngOnInit() {
    this.isAllTab = this.entityType === ALL_RESULTS_SEARCH_TYPE;
    this.config = searchEntityBySearchType[this.entityType];

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.startDate && moment(qp.startDate).isValid()) {
          this.startDate = qp.startDate;
        } else {
          this.startDate = null;
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          this.endDate = qp.endDate;
        } else {
          this.endDate = null;
        }

        this.filterResults();
      });

    this.store.select(searchResultsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.resultsList = this.isAllTab
          ? this.mergeAllResults(data?.resultsByType || {})
          : (data?.resultsByType?.[this.entityType] || []);
        this.filterResults();
      });

    this.commonService.resetPaginator
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.currentPage = 1;
      });
  }

  /**
   * Flattens every entity type's results into one relevance-ranked list.
   * `_score` is the api's blended relevance/recency value (search-service.js
   * overwrites the raw BM25 score with it), so a single sort already accounts
   * for both. Rows are NOT decorated with their registry entry: filterResults
   * deep-clones via JSON, which would strip the entry's titleOf/snippetOf/...
   * functions and leave every generic card blank. The effect stamps
   * `searchType` on each result, which is enough to look the entry up.
   */
  mergeAllResults(resultsByType: Record<string, any[]>): any[] {
    const merged: any[] = [];
    SEARCH_ENTITIES.forEach(entity => {
      merged.push(...(resultsByType?.[entity.searchType] || []));
    });
    merged.sort((a, b) => (b?._score || 0) - (a?._score || 0));
    return merged.slice(0, this.allTabLimit);
  }

  /** Registry entry for a row — per-result on the All tab, fixed elsewhere. */
  configFor(result: any): SearchEntityConfig {
    return (this.isAllTab ? searchEntityBySearchType[result?.searchType] : this.config) || this.config;
  }

  get isServerTruncated(): boolean {
    // The All tab's cap is ours, not the api's — its own footnote explains it.
    return !this.isAllTab && this.resultsList?.length >= this.apiResultWindow;
  }

  get isAllTabCapped(): boolean {
    return this.isAllTab && this.resultsList?.length >= this.allTabLimit;
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.updatePaginatedResultsList();
  }

  updatePaginatedResultsList(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filterResultsList.length);
    this.paginatedResultsList = this.filterResultsList.slice(startIndex, endIndex);
  }

  openResult(result: any): void {
    const config = this.configFor(result);
    if (config?.panelComponent) {
      // Stays on /search: opens the right slide-in panel (legacy issues behavior).
      this.router.navigate([], {
        queryParams: {
          projectId: result?.project?.id,
          selectedId: result?.id,
        },
        queryParamsHandling: 'merge',
      });
      setTimeout(() => {
        this.searchService.openRightPanel.next({
          component: config.panelComponent,
          data: {
            ...result,
          },
        });
      });
    } else if (config?.navigationFor) {
      const nav = config.navigationFor(result);

      if (nav) {
        // Remember this results page so the header can offer a way back —
        // target pages rewrite their own query params, so browser Back alone
        // takes several presses to return here.
        this.commonService.searchResultsUrl = this.router.url;
        // Intentionally no queryParamsHandling merge: /search params like
        // startDate/endDate would otherwise leak into the target page filters.
        this.router.navigate(nav.commands, { queryParams: nav.queryParams });
      }
    }
  }

  filterResults() {
    // Per-type date field (the All tab mixes types), falling back to the
    // effect-stamped createdAt so results with a missing date aren't
    // silently dropped.
    const dateField = (o: any) => this.configFor(o)?.dateField;
    const dateOf = (o: any) => o?.[dateField(o)] ?? o?.createdAt;
    let filterResultsList = JSON.parse(JSON.stringify(this.resultsList || []));

    if (this.startDate) {
      filterResultsList = filterResultsList
        .filter(o => moment(dateOf(o), 'YYYY-MM-DD')
          .isSameOrAfter(moment(this.startDate, 'YYYY-MM-DD'))) || [];
    }

    if (this.endDate) {
      filterResultsList = filterResultsList
        .filter(o => moment(dateOf(o), 'YYYY-MM-DD')
          .isSameOrBefore(moment(this.endDate, 'YYYY-MM-DD')),
        ) || [];
    }

    this.filterResultsList = filterResultsList;
    this.onPageChanged(1);
    this.commonService.resetPaginator.next(true);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
